import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import BrushIcon from '@mui/icons-material/Brush';
import UndoIcon from '@mui/icons-material/Undo';
import BackspaceIcon from '@mui/icons-material/Backspace';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import { PointsCheckDialog } from '../shared/PointsCheckDialog';
import { usePointsCheck } from '../shared/usePointsCheck';

// ============ 类型定义 ============

type ImageType = 'obj' | 'bg';

interface RefImage {
  url: string;
  type: ImageType;
  localFile?: File;
}

type TaskStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

type UiStatus = 'idle' | 'uploading' | 'submitting';

interface CreateTaskResponse {
  task_id: string;
}

interface TaskStatusResponse {
  status: TaskStatus;
  video_url?: string;
  error?: string;
}

type SizeOption = '1280*720' | '720*1280' | '960*960' | '832*1088' | '1088*832';

interface VideoTask {
  id: string;
  status: TaskStatus;
  videoUrl?: string;
  error?: string;
  prompt: string;
  createdAt: number;
}

interface AiVideoEditPageProps {
  embedded?: boolean;
}

type FunctionMode = 'image_reference' | 'video_repainting' | 'video_edit' | 'video_extension' | 'video_outpainting';

type ControlCondition = 'posebodyface' | 'posebody' | 'depth' | 'scribble';

type MaskType = 'tracking' | 'fixed';
type ExpandMode = 'hull' | 'bbox' | 'original';

// ============ 主组件 ============

export default function AiVideoEditPage({ embedded = false }: AiVideoEditPageProps = {}) {
  const { refreshUser, user } = useAuth();
  const { checkPoints, showDialog, dialogProps, closeDialog } = usePointsCheck();

  // -------- 宽度状态 (解决 100vw 滚动条问题) --------
  const [screenWidth, setScreenWidth] = useState(() =>
    typeof document !== 'undefined' ? `${document.documentElement.clientWidth}px` : '100vw'
  );

  useEffect(() => {
    const updateWidth = () => {
        setScreenWidth(`${document.documentElement.clientWidth}px`);
    };
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // -------- 表单状态 --------
  const [functionMode, setFunctionMode] = useState<FunctionMode>('image_reference');
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState<SizeOption>('1280*720');
  const [refImages, setRefImages] = useState<RefImage[]>([]);
  
  // video_repainting 专用状态
  const [videoUrl, setVideoUrl] = useState('');
  const [localVideoPreviewUrl, setLocalVideoPreviewUrl] = useState('');
  const [controlCondition, setControlCondition] = useState<ControlCondition>('depth');
  const [strength, setStrength] = useState<number>(1.0);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // video_edit 专用状态
  const [maskImageUrl, setMaskImageUrl] = useState('');
  const [maskVideoUrl, setMaskVideoUrl] = useState('');
  const [maskFrameId, setMaskFrameId] = useState<number | string>(1);
  const [maskType, setMaskType] = useState<MaskType>('tracking');
  const [expandRatio, setExpandRatio] = useState<number>(0.05);
  const [expandMode, setExpandMode] = useState<ExpandMode>('hull');
  const [activeMaskInput, setActiveMaskInput] = useState<'image' | 'video' | null>(null);

  // video_extension 专用状态
  const [extFirstFrame, setExtFirstFrame] = useState('');
  const [extLastFrame, setExtLastFrame] = useState('');
  const [extFirstClip, setExtFirstClip] = useState('');
  const [extLastClip, setExtLastClip] = useState('');
  const [extVideoUrl, setExtVideoUrl] = useState('');
  const [extControlCondition, setExtControlCondition] = useState<'posebodyface' | 'depth' | ''>('');
  const [extPromptExtend, setExtPromptExtend] = useState(false);
  const [extSeed, setExtSeed] = useState<number | undefined>(undefined);
  const [extWatermark, setExtWatermark] = useState(false);
  const [uploadingExt, setUploadingExt] = useState(false);

  // video_outpainting 专用状态
  const [outTopScale, setOutTopScale] = useState<number>(1.0);
  const [outBottomScale, setOutBottomScale] = useState<number>(1.0);
  const [outLeftScale, setOutLeftScale] = useState<number>(1.0);
  const [outRightScale, setOutRightScale] = useState<number>(1.0);

  // -------- UI 状态 --------
  const [uiStatus, setUiStatus] = useState<UiStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // -------- 任务列表状态 --------
  const [tasks, setTasks] = useState<VideoTask[]>([]);
  
  // 滚动容器引用
  const resultsScrollRef = useRef<HTMLDivElement | null>(null);
  
  // 保持组件挂载状态的引用，防止卸载后更新状态
  const isMountedRef = useRef(true);
  // 用于使进行中的上传结果失效的序列号
  const videoUploadSeqRef = useRef(0);
  // 主视频上传可取消的控制器引用
  const videoUploadAbortRef = useRef<AbortController | null>(null);
  // 主视频文件输入引用（避免使用 label 触发）
  const mainVideoInputRef = useRef<HTMLInputElement | null>(null);
  // 掩码本地预览与上传控制
  const [localMaskImagePreviewUrl, setLocalMaskImagePreviewUrl] = useState('');
  const [localMaskVideoPreviewUrl, setLocalMaskVideoPreviewUrl] = useState('');
  const maskImageUploadSeqRef = useRef(0);
  const maskVideoUploadSeqRef = useRef(0);
  const maskImageUploadAbortRef = useRef<AbortController | null>(null);
  const maskVideoUploadAbortRef = useRef<AbortController | null>(null);
  const maskImageInputRef = useRef<HTMLInputElement | null>(null);
  const maskVideoInputRef = useRef<HTMLInputElement | null>(null);
  // Mask Editor Input
  const maskEditorInputRef = useRef<HTMLInputElement | null>(null);
  // 掩码编辑器弹窗与绘制状态
  const [showMaskEditor, setShowMaskEditor] = useState(false);
  const [maskEditorMode, setMaskEditorMode] = useState<'image' | 'video'>('image');
  const [maskEditorVideoUrl, setMaskEditorVideoUrl] = useState('');
  const [maskEditorImageUrl, setMaskEditorImageUrl] = useState('');

  const [maskTool, setMaskTool] = useState<'pen'|'eraser'|'move'>('pen');
  const [maskBrushSize, setMaskBrushSize] = useState(18);
  const baseCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{x:number,y:number}|null>(null);
  const maskSnapshotsRef = useRef<ImageData[]>([]);
  const currentStrokeRef = useRef<{x:number, y:number}[]>([]);
  const [isAutoFill, setIsAutoFill] = useState(false);
  // 新增：动态跟踪开关（用于生成的掩码是否作为动态跟踪参考）
  const [isDynamicTracking, setIsDynamicTracking] = useState(false);

  const [editorVideoState, setEditorVideoState] = useState<{
    file: File | null;
    duration: number;
    currentTime: number;
    isPlaying: boolean;
  }>({ file: null, duration: 0, currentTime: 0, isPlaying: false });
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const openMaskEditor = (mode: 'image' | 'video' = 'image') => { 
    setMaskEditorMode(mode);
    setMaskEditorVideoUrl('');
    setMaskEditorImageUrl('');
    handClearMaskEditor(); // Reset canvas
    setShowMaskEditor(true); 
    setMaskTool('pen');
    setIsAutoFill(false);
    setIsDynamicTracking(mode === 'video'); // Default to true for video for convenience? Or let user decide. Let's defaut false.
  };
  const closeMaskEditor = () => { setShowMaskEditor(false); };

  const handleMaskEditorFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 如果当前模式是 video，但上传的是图片... 或者反之
    // 假设 input accept 已经做好了限制
    const url = URL.createObjectURL(file);
    e.target.value = '';

    if (maskEditorMode === 'video') {
         setMaskEditorVideoUrl(url);
         setEditorVideoState(prev => ({ ...prev, file: file, currentTime: 0, duration: 0, isPlaying: false }));
         // Clear Image Canvas if any
         setMaskEditorImageUrl('');
         const base = baseCanvasRef.current;
         if(base) { const ctx = base.getContext('2d'); ctx?.clearRect(0,0,base.width, base.height); }
    } else {
        setMaskEditorImageUrl(url);
        setMaskEditorVideoUrl('');
        setTimeout(() => {
          const img = new Image();
          img.onload = () => {
            const maxW = 1200; const maxH = 800; // Increased size
            let w = img.width, h = img.height;
            const scale = Math.min(maxW / w, maxH / h, 1);
            w = Math.round(w * scale); h = Math.round(h * scale);
            const base = baseCanvasRef.current; const draw = drawCanvasRef.current; const mask = maskCanvasRef.current;
            if (!base || !draw || !mask) return;
            [base, draw, mask].forEach(c => { c.width = w; c.height = h; });
            const bctx = base.getContext('2d'); if (!bctx) return;
            bctx.clearRect(0,0,w,h);
            bctx.drawImage(img, 0, 0, w, h);
            const dctx = draw.getContext('2d'); const mctx = mask.getContext('2d');
            if (!dctx || !mctx) return;
            dctx.clearRect(0,0,w,h);
            mctx.clearRect(0,0,w,h);
            maskSnapshotsRef.current = [mctx.getImageData(0,0,w,h)];
          };
          img.src = url;
        }, 0);
    }
  };

  const getCanvasPoint = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = drawCanvasRef.current; if (!canvas) return {x:0,y:0};
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { 
        x: (ev.clientX - rect.left) * scaleX, 
        y: (ev.clientY - rect.top) * scaleY 
    };
  };

  const onDrawStart = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    if ((!maskEditorImageUrl && !maskEditorVideoUrl) || maskTool === 'move') return;
    const p = getCanvasPoint(ev);
    isDrawingRef.current = true; lastPointRef.current = p;
    currentStrokeRef.current = [p];
    const mask = maskCanvasRef.current; if (!mask) return;
    const mctx = mask.getContext('2d'); if (!mctx) return;
    maskSnapshotsRef.current.push(mctx.getImageData(0,0,mask.width,mask.height));
  };

  const drawLine = (ctx: CanvasRenderingContext2D, from: {x:number,y:number}, to: {x:number,y:number}, color: string, composite: GlobalCompositeOperation = 'source-over') => {
    ctx.globalCompositeOperation = composite;
    ctx.strokeStyle = color; ctx.lineWidth = maskBrushSize; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  };

  const onDrawMove = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || !lastPointRef.current) return;
    const p = getCanvasPoint(ev);
    currentStrokeRef.current.push(p);
    const draw = drawCanvasRef.current; const mask = maskCanvasRef.current;
    if (!draw || !mask) return;
    const dctx = draw.getContext('2d'); const mctx = mask.getContext('2d');
    if (!dctx || !mctx) return;
    // 画红线（仅视觉层）
    if (maskTool === 'pen') {
      drawLine(dctx, lastPointRef.current, p, '#ff3b3b');
      // 白色涂抹到掩码层
      drawLine(mctx, lastPointRef.current, p, '#ffffff');
    } else {
      // 橡皮：清除视觉层红线 + 清除掩码层白色
      drawLine(dctx, lastPointRef.current, p, 'rgba(0,0,0,1)', 'destination-out');
      drawLine(mctx, lastPointRef.current, p, 'rgba(0,0,0,1)', 'destination-out');
    }
    lastPointRef.current = p;
  };

  const onDrawEnd = () => { 
    if (isDrawingRef.current && isAutoFill && maskTool === 'pen') {
       const points = currentStrokeRef.current;
       if (points.length > 2) {
          // Fill Mask (White)
          const mask = maskCanvasRef.current;
          const mctx = mask?.getContext('2d');
          if (mctx) {
             mctx.fillStyle = '#ffffff';
             mctx.beginPath();
             const start = points[0];
             mctx.moveTo(start.x, start.y);
             for(let i=1; i<points.length; i++) mctx.lineTo(points[i].x, points[i].y);
             mctx.closePath();
             mctx.fill();
          }

          // Fill Visual (Red Semitransparent)
          const draw = drawCanvasRef.current; 
          const dctx = draw?.getContext('2d');
          if (dctx) {
             dctx.fillStyle = 'rgba(255, 59, 59, 0.5)'; 
             dctx.beginPath();
             const start = points[0];
             dctx.moveTo(start.x, start.y);
             for(let i=1; i<points.length; i++) dctx.lineTo(points[i].x, points[i].y);
             dctx.closePath();
             dctx.fill();
          }
       }
    }
    isDrawingRef.current = false; 
    lastPointRef.current = null; 
    currentStrokeRef.current = [];
  };

  const onUndoMask = () => {
    const mask = maskCanvasRef.current; if (!mask) return;
    const mctx = mask.getContext('2d'); if (!mctx) return;
    if (maskSnapshotsRef.current.length > 0) {
      // 弹出最新快照（当前状态的起点），恢复到它
      // 注意：上面的 onDrawStart 实际上是在画之前 push 了当前状态。
      // 所以 undo 应该是 pop 并 restore。
      const prev = maskSnapshotsRef.current.pop();
      if (prev) mctx.putImageData(prev, 0, 0);
    }
    // 视觉层需要重绘？因为视觉层没有保存快照。
    // 当前简化逻辑：undo 会清除所有视觉层红线，因为我们没存视觉层的历史。
    // 这是一个已知限制，但符合 mvp。用户可能看到红线消失但 mask 恢复了。
    // 为了体验更好，我们直接清空视觉层。
    const draw = drawCanvasRef.current; const dctx = draw?.getContext('2d');
    if (draw && dctx) dctx.clearRect(0,0,draw.width,draw.height);
  };

  const handClearMaskEditor = () => {
    setMaskEditorImageUrl('');
    setMaskEditorVideoUrl('');
    // Clear Input
    if (maskEditorInputRef.current) {
        maskEditorInputRef.current.value = '';
    }
    const base = baseCanvasRef.current;
    const draw = drawCanvasRef.current;
    const mask = maskCanvasRef.current;
    if(base) { const ctx = base.getContext('2d'); ctx?.clearRect(0,0,base.width, base.height); }
    if(draw) { const ctx = draw.getContext('2d'); ctx?.clearRect(0,0,draw.width, draw.height); }
    if(mask) { const ctx = mask.getContext('2d'); ctx?.clearRect(0,0,mask.width, mask.height); }
    maskSnapshotsRef.current = [];
  };

  const generateVideoMask = async () => {
    const file = editorVideoState.file;
    const maskCanvas = maskCanvasRef.current;
    if (!file || !maskCanvas) {
        alert("缺少视频文件或未绘制掩码");
        return;
    }

    setUiStatus('uploading');
    
    try {
        const width = maskCanvas.width;
        const height = maskCanvas.height;
        
        const mixCanvas = document.createElement('canvas');
        mixCanvas.width = width;
        mixCanvas.height = height;
        const mixCtx = mixCanvas.getContext('2d');
        if (!mixCtx) throw new Error("Canvas Context Failed");

        const tempVideo = document.createElement('video');
        tempVideo.src = URL.createObjectURL(file);
        tempVideo.muted = true;
        tempVideo.playsInline = true;
        await new Promise((resolve) => {
            tempVideo.onloadedmetadata = () => resolve(true);
            setTimeout(() => resolve(false), 2000); 
        });
        
        const stream = mixCanvas.captureStream(30);
        const recorder = new MediaRecorder(stream, { mimeType: 'video/webm; codecs=vp9' });
        const chunks: Blob[] = [];
        recorder.ondataavailable = (e) => { if(e.data.size > 0) chunks.push(e.data); };
        
        const recordingPromise = new Promise<Blob>((resolve) => {
            recorder.onstop = () => {
                const blob = new Blob(chunks, { type: 'video/webm' });
                resolve(blob);
            };
        });

        recorder.start();

        tempVideo.currentTime = 0;
        await tempVideo.play();
        
        const drawLoop = () => {
             mixCtx.fillStyle = '#000000';
             mixCtx.fillRect(0, 0, width, height);
             // 绘制白色掩码 (固定位置 - 模拟静态目标跟踪)
             mixCtx.drawImage(maskCanvas, 0, 0);
             if (!tempVideo.paused && !tempVideo.ended) {
                 requestAnimationFrame(drawLoop);
             }
        };
        drawLoop();
        
        await new Promise((resolve) => {
            tempVideo.onended = resolve;
        });
        
        await new Promise(r => setTimeout(r, 200));

        recorder.stop();
        const recordedBlob = await recordingPromise;
        const recordedFile = new File([recordedBlob], "mask_generated.webm", { type: 'video/webm' });
        
        // Upload the generated mask video
        const token = localStorage.getItem('access_token');
        const formData = new FormData();
        formData.append('file', recordedFile);
        
        const response = await fetch(`${API_BASE}/api/upload/image`, {
             method: 'POST',
             headers: token ? { 'Authorization': `Bearer ${token}` } : {},
             body: formData,
        });
        
        if (!response.ok) throw new Error('Mask upload failed');
        const data = await response.json();
        const serverUrl = data.url || data.image_url;

        setMaskVideoUrl(serverUrl);
        setLocalMaskVideoPreviewUrl(URL.createObjectURL(recordedFile));
        setActiveMaskInput('video');
        
        // Auto set params
        if (isDynamicTracking) {
             setMaskType('tracking');
        } else {
             setMaskType('fixed');
        }

        setUiStatus('idle');
        closeMaskEditor();
        if (isDynamicTracking) {
            alert("已设置为动态跟踪模式。注意：本地预览中的掩码仅显示初始位置（静止），云端AI将自动跟踪该区域的运动。");
        } else {
            alert("掩码视频生成成功! (固定位置掩码已应用)");
        }

    } catch (e) {
        console.error(e);
        setUiStatus('idle');
        alert("生成失败，请重试");
    }
  };

  const handleEditorGenerate = async () => {
      if (maskEditorMode === 'video') {
         // 无论是动态还是静态，对于前端来说都是生成一个“基准视频”
         // 静态模式：基准视频就是最终掩码（全黑+白圈在固定位置）
         // 动态模式：基准视频提供第1帧的位置，后端根据mask_type=tracking来计算后续帧
         // 所以统一走 generateVideoMask 流程，但会设置不同的 maskParam
         await generateVideoMask();
      } else {
        const mask = maskCanvasRef.current; if (!mask) return;
        try {
          const dataUrl = mask.toDataURL('image/png');
          setMaskImageUrl(dataUrl);
          setLocalMaskImagePreviewUrl(dataUrl);
          setActiveMaskInput('image');
          closeMaskEditor();
        } catch (e) {
          console.error(e);
        }
      }
  };

  // 添加一个 Ref 来保存 Editor 中的 File
  const maskEditorFileRef = useRef<File | null>(null);

  // 修改 handleMaskEditorFileUpload 来保存 File
  const handleMaskEditorFileUploadWrapper = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          maskEditorFileRef.current = file;
      }
      handleMaskEditorFileUpload(e);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 自动滚动到底部 (当任务列表变化时)
  useEffect(() => {
    const el = resultsScrollRef.current;
    if (!el) return;
    // 使用 requestAnimationFrame 确保在渲染后滚动
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [tasks.length]);

  // ============ 样式定义 (参考 Nanobana 模式) ============
  
  const rainbowMaskContainerSx = {
    '@keyframes rainbowShift': {
      '0%': { backgroundPosition: '0% 50%' },
      '50%': { backgroundPosition: '100% 50%' },
      '100%': { backgroundPosition: '0% 50%' },
    },
    '@keyframes breatheOpacity': {
      '0%': { opacity: 0.5 },
      '50%': { opacity: 0.65 },
      '100%': { opacity: 0.5 },
    },
  } as const;

  const placeholderCardSx = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 2,
    border: '1px dashed #cbd5e1',
    bgcolor: '#f8fafc',
    width: '100%',
    height: '100%',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background:
        'linear-gradient(120deg, rgba(255,0,128,0.35), rgba(0,200,255,0.35), rgba(0,255,128,0.35), rgba(255,230,0,0.35), rgba(255,0,128,0.35))',
      backgroundSize: '300% 300%',
      animation: 'rainbowShift 2.6s ease-in-out infinite',
      filter: 'blur(0px)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'rgba(255,255,255,0.55)',
      animation: 'breatheOpacity 2.2s ease-in-out infinite',
    },
  } as const;

  // ============ 图片上传逻辑 ============

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (refImages.length + files.length > 3) {
      setErrorMsg('最多只能上传3张图片');
      return;
    }

    setUiStatus('uploading');
    setErrorMsg('');

    try {
      const token = localStorage.getItem('access_token');
      const newImages: RefImage[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE}/api/upload/image`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`图片上传失败: ${response.statusText}`);
        }

        const data = await response.json();
        const imageUrl = data.url || data.image_url;

        if (!imageUrl) {
          throw new Error('上传响应中未找到图片URL');
        }

        newImages.push({
          url: imageUrl,
          type: 'obj',
          localFile: file,
        });
      }

      setRefImages([...refImages, ...newImages]);
      setUiStatus('idle');
    } catch (error: any) {
      setErrorMsg(error.message || '图片上传失败');
      setUiStatus('idle');
    }

    event.target.value = '';
  };

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingVideo(true);
    setErrorMsg('');
    
    try {
        const token = localStorage.getItem('access_token');
        const file = files[0];
        // 记录本次上传序列号，清除时会使其失效
        const mySeq = ++videoUploadSeqRef.current;
        // 建立可取消的上传控制器
        const controller = new AbortController();
        videoUploadAbortRef.current = controller;
        // 先本地预览，立即显示用户选择的视频
        const objectUrl = URL.createObjectURL(file);
        if (localVideoPreviewUrl) {
          try { URL.revokeObjectURL(localVideoPreviewUrl); } catch {}
        }
        setLocalVideoPreviewUrl(objectUrl);
        const formData = new FormData();
        formData.append('file', file);
    
        const response = await fetch(`${API_BASE}/api/upload/image`, { // Reuse image upload for now as it returns signed url
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData,
          signal: controller.signal,
        });
    
        if (!response.ok) throw new Error(`视频上传失败: ${response.statusText}`);
    
        const data = await response.json();
        const url = data.url || data.image_url;
        if (!url) throw new Error('未获取到视频URL');
        // 若用户在上传过程中点击了清除，当前 mySeq 将失效，不设置 URL
        if (mySeq === videoUploadSeqRef.current) {
          setVideoUrl(url);
          // 上传拿到 OSS URL 后，切换到远端 URL 并释放本地预览
          if (objectUrl) {
            try { URL.revokeObjectURL(objectUrl); } catch {}
          }
          setLocalVideoPreviewUrl('');
        }
        // 上传成功后，不显示成功提示词
    } catch (error: any) {
        if (error?.name === 'AbortError' || String(error?.message || '').toLowerCase().includes('aborted')) {
          // 用户点击清除导致的中止，不提示错误
        } else {
          setErrorMsg(error.message || '视频上传失败');
        }
    } finally {
      setUploadingVideo(false);
      event.target.value = '';
      // 清理控制器
      videoUploadAbortRef.current = null;
    }
  };

  // 清除当前主视频（本地预览与远端 URL），并使进行中的上传失效
  const handleClearMainVideo = () => {
    // 递增序列使进行中的上传结果失效
    videoUploadSeqRef.current++;
    // 若存在进行中的上传，请求中止，并关闭上传中状态
    if (videoUploadAbortRef.current) {
      try { videoUploadAbortRef.current.abort('user-clear'); } catch {}
      videoUploadAbortRef.current = null;
    }
    setUploadingVideo(false);
    if (localVideoPreviewUrl) {
      try { URL.revokeObjectURL(localVideoPreviewUrl); } catch {}
    }
    setLocalVideoPreviewUrl('');
    setVideoUrl('');
  };

  const handleMaskImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
  
      setUiStatus('uploading');
      setErrorMsg('');
  
      try {
          const token = localStorage.getItem('access_token');
          const file = files[0];
        const mySeq = ++maskImageUploadSeqRef.current;
        const controller = new AbortController();
        maskImageUploadAbortRef.current = controller;
        const objectUrl = URL.createObjectURL(file);
        if (localMaskImagePreviewUrl) {
        try { URL.revokeObjectURL(localMaskImagePreviewUrl); } catch {}
        }
        setLocalMaskImagePreviewUrl(objectUrl);
          const formData = new FormData();
          formData.append('file', file);
  
          const response = await fetch(`${API_BASE}/api/upload/image`, {
              method: 'POST',
              headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData,
          signal: controller.signal,
          });
  
          if (!response.ok) throw new Error(`掩码图片上传失败: ${response.statusText}`);
  
          const data = await response.json();
          const url = data.url || data.image_url;
          if (!url) throw new Error('未获取到图片URL');
        if (mySeq === maskImageUploadSeqRef.current) {
        setMaskImageUrl(url);
        setMaskVideoUrl(''); // 互斥
        try { URL.revokeObjectURL(objectUrl); } catch {}
        setLocalMaskImagePreviewUrl('');
        }
          // 上传成功后，不显示成功提示词
      } catch (error: any) {
          if (error?.name === 'AbortError' || String(error?.message || '').toLowerCase().includes('aborted')) {
            // 用户点击清除导致的中止，不提示错误
          } else {
            setErrorMsg(error.message || '掩码图片上传失败');
          }
      } finally {
          setUiStatus('idle');
          event.target.value = '';
        maskImageUploadAbortRef.current = null;
      }
  };

  const handleMaskVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
  
      setUploadingVideo(true);
      setErrorMsg('');
  
      try {
          const token = localStorage.getItem('access_token');
          const file = files[0];
          const mySeq = ++maskVideoUploadSeqRef.current;
          const controller = new AbortController();
          maskVideoUploadAbortRef.current = controller;
          const objectUrl = URL.createObjectURL(file);
          if (localMaskVideoPreviewUrl) {
            try { URL.revokeObjectURL(localMaskVideoPreviewUrl); } catch {}
          }
          setLocalMaskVideoPreviewUrl(objectUrl);
          const formData = new FormData();
          formData.append('file', file);
  
          const response = await fetch(`${API_BASE}/api/upload/image`, { // Reuse endpoint
              method: 'POST',
              headers: token ? { 'Authorization': `Bearer ${token}` } : {},
              body: formData,
              signal: controller.signal,
          });
  
          if (!response.ok) throw new Error(`掩码视频上传失败: ${response.statusText}`);
  
          const data = await response.json();
          const url = data.url || data.image_url;
          if (!url) throw new Error('未获取到视频URL');
          if (mySeq === maskVideoUploadSeqRef.current) {
            setMaskVideoUrl(url);
            setMaskImageUrl(''); // 互斥
            try { URL.revokeObjectURL(objectUrl); } catch {}
            setLocalMaskVideoPreviewUrl('');
          }
          // 上传成功后，不显示成功提示词
      } catch (error: any) {
          if (error?.name === 'AbortError' || String(error?.message || '').toLowerCase().includes('aborted')) {
            // 用户点击清除导致的中止，不提示错误
          } else {
            setErrorMsg(error.message || '掩码视频上传失败');
          }
      } finally {
          setUploadingVideo(false);
          event.target.value = '';
          maskVideoUploadAbortRef.current = null;
      }
  };

  const handleClearMaskImage = () => {
    maskImageUploadSeqRef.current++;
    if (maskImageUploadAbortRef.current) {
      try { maskImageUploadAbortRef.current.abort('user-clear'); } catch {}
      maskImageUploadAbortRef.current = null;
    }
    if (localMaskImagePreviewUrl) {
      try { URL.revokeObjectURL(localMaskImagePreviewUrl); } catch {}
    }
    setLocalMaskImagePreviewUrl('');
    setMaskImageUrl('');
  };

  const handleClearMaskVideo = () => {
    maskVideoUploadSeqRef.current++;
    if (maskVideoUploadAbortRef.current) {
      try { maskVideoUploadAbortRef.current.abort('user-clear'); } catch {}
      maskVideoUploadAbortRef.current = null;
    }
    if (localMaskVideoPreviewUrl) {
      try { URL.revokeObjectURL(localMaskVideoPreviewUrl); } catch {}
    }
    setLocalMaskVideoPreviewUrl('');
    setMaskVideoUrl('');
  };

  const handleToggleImageType = (index: number) => {
    const newImages = [...refImages];
    const currentType = newImages[index].type;

    if (currentType === 'obj') {
      const bgCount = newImages.filter((img) => img.type === 'bg').length;
      if (bgCount >= 1) {
        setErrorMsg('最多只能有1个背景图片');
        return;
      }
      newImages[index].type = 'bg';
    } else {
      newImages[index].type = 'obj';
    }

    setRefImages(newImages);
    setErrorMsg('');
  };

  // 删除参考图片
  const handleDeleteImage = (index: number) => {
    setRefImages((prev) => prev.filter((_, i) => i !== index));
  };

  // 视频延展资源上传（首帧/尾帧图片、首段/尾段视频、参考视频）
  const handleExtFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setter: (url: string) => void,
    kind: 'image' | 'video'
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUiStatus('uploading');
    setErrorMsg('');
    try {
      const token = localStorage.getItem('access_token');
      const file = files[0];
      const formData = new FormData();
      formData.append('file', file);

      // 复用后端统一上传接口（返回 OSS URL）
      const response = await fetch(`${API_BASE}/api/upload/image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) throw new Error(`${kind === 'image' ? '图片' : '视频'}上传失败: ${response.statusText}`);

      const data = await response.json();
      const url = data.url || data.image_url;
      if (!url) throw new Error(`未获取到${kind === 'image' ? '图片' : '视频'}URL`);

      setter(url);
      // 上传成功后，不显示成功提示词
    } catch (error: any) {
      setErrorMsg(error.message || `${kind === 'image' ? '图片' : '视频'}上传失败`);
    } finally {
      setUiStatus('idle');
      event.target.value = '';
    }
  };
  // 表单校验
  const validateForm = (): string | null => {
    // Prompt 必填
    if (!prompt.trim()) return '请输入 Prompt';

    if (functionMode === 'image_reference') {
      // 至少1张参考图，最多3张（详细限制在上传处已控制）
      if (refImages.length < 1) return '请上传至少1张参考图片';
      if (refImages.length > 3) return '最多3张参考图片';
    } else if (functionMode === 'video_repainting') {
      // video_repainting
      if (uploadingVideo) return '视频正在上传，请稍候';
      if (!videoUrl) return '请上传原视频';
      if (refImages.length > 1) return '视频重绘模式最多只支持1张参考图';
    } else if (functionMode === 'video_edit') {
      // video_edit
      if (uploadingVideo) return '视频正在上传，请稍候';
      if (!videoUrl) return '请上传原视频';
      if (refImages.length > 1) return '视频编辑模式最多只支持1张参考图';
      // 掩码可选（图/视频二选一），不做强制
    } else if (functionMode === 'video_extension') {
      // video_extension
      if (extVideoUrl && !extControlCondition)
        return '输入视频URL时，必须选择控制条件 (Control Condition)';
    } else if (functionMode === 'video_outpainting') {
      // video_outpainting
      if (uploadingVideo) return '视频正在上传，请稍候';
      if (!videoUrl) return '请上传原视频';
    }

    return null;
  };

  // ============ 提交任务 ============

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    // 积分检查：视频生成需要30积分
    if (!checkPoints(30)) {
      return;
    }

    setUiStatus('submitting');
    setErrorMsg('');
    setSuccessMsg('');

    try {
      let requestBody: any = {
        function: functionMode,
        prompt: prompt.trim(),
      };

      if (functionMode === 'image_reference') {
          requestBody.size = size;
          requestBody.ref_images = refImages.map((img) => ({
              url: img.url,
              type: img.type,
          }));
      } else if (functionMode === 'video_repainting') {
          // video_repainting
          requestBody.video_url = videoUrl;
          if (refImages.length > 0) {
              requestBody.ref_image_url = refImages[0].url;
          }
          requestBody.control_condition = controlCondition;
          requestBody.strength = strength;
      } else if (functionMode === 'video_edit') {
          // video_edit
          requestBody.video_url = videoUrl;
          if (refImages.length > 0) {
              requestBody.ref_image_url = refImages[0].url;
          }
          
          if (maskImageUrl) {
              requestBody.mask_image_url = maskImageUrl;
              requestBody.mask_frame_id = Number(maskFrameId) || 1;
          } else if (maskVideoUrl) {
              requestBody.mask_video_url = maskVideoUrl;
          }

          requestBody.mask_type = maskType;
          if (maskType === 'tracking') {
              requestBody.expand_ratio = expandRatio;
              requestBody.expand_mode = expandMode;
          }
          
          if (controlCondition) {
             requestBody.control_condition = controlCondition;
          }
      } else if (functionMode === 'video_extension') {
          // video_extension
          if (extVideoUrl && extControlCondition) {
              requestBody.video_url = extVideoUrl;
              requestBody.control_condition = extControlCondition;
          }
          if (extFirstFrame) requestBody.first_frame_image_url = extFirstFrame;
          if (extLastFrame) requestBody.last_frame_image_url = extLastFrame;
          if (extFirstClip) requestBody.first_clip_video_url = extFirstClip;
          if (extLastClip) requestBody.last_clip_video_url = extLastClip;
          if (extPromptExtend) requestBody.prompt_extend = true;
          if (extSeed !== undefined) requestBody.seed = extSeed;
          if (extWatermark) requestBody.watermark = true;
      } else if (functionMode === 'video_outpainting') {
          // video_outpainting
          requestBody.video_url = videoUrl;
          requestBody.top = outTopScale;
          requestBody.bottom = outBottomScale;
          requestBody.left = outLeftScale;
          requestBody.right = outRightScale;
      }

      // 调用创建任务API
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/proxy/wanx/vace/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `创建任务失败 (${response.status})`);
      }

      const data: CreateTaskResponse = await response.json();
      const taskId = data.task_id;

      // 刷新用户积分
      await refreshUser();

      // 添加新任务到列表
      const newTask: VideoTask = {
        id: taskId,
        status: 'PENDING',
        prompt: prompt.trim(),
        createdAt: Date.now()
      };
      
      setTasks((prev) => [newTask, ...prev]);
      setSuccessMsg('任务已提交，正在生成中...');
      
      // 开始轮询
      pollTask(taskId);
      
    } catch (error: any) {
      setErrorMsg(error?.message || '提交失败');
    } finally {
      setUiStatus('idle');
    }
  };

  // ============ 轮询任务状态 (支持多任务并发) ============

  const pollTask = async (tid: string) => {
    const maxRetries = 100;
    let retries = 0;

    const check = async () => {
      if (!isMountedRef.current) return;
      
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE}/api/proxy/wanx/vace/status/${tid}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
            console.warn(`Polling failed for ${tid}: ${response.statusText}`);
        } else {
            const data: TaskStatusResponse = await response.json();
            
            // 更新任务状态
            setTasks((prev) => prev.map(t => {
                if (t.id !== tid) return t;
                return {
                    ...t,
                    status: data.status,
                    videoUrl: data.video_url,
                    error: data.error
                };
            }));

            if (data.status === 'SUCCEEDED' || data.status === 'FAILED') {
                // 任务完成，刷新用户积分
                await refreshUser();
                return;
            }
        }
      } catch (e) {
          console.error(e);
      }

      retries++;
      if (retries < maxRetries) {
          setTimeout(check, 3000);
      } else {
          setTasks((prev) => prev.map(t => {
              if (t.id !== tid) return t;
              return { ...t, status: 'FAILED', error: '轮询超时' };
          }));
      }
    };

    setTimeout(check, 3000);
  };

  // ============ 重置表单 ============

  const handleReset = () => {
    setPrompt('');
    setSize('1280*720');
    setRefImages([]);
    setVideoUrl(''); // Reset videoUrl for repainting/edit
    setLocalVideoPreviewUrl('');
    
    // Reset Edit Utils
    setMaskImageUrl('');
    setMaskVideoUrl('');
    setMaskFrameId(1);
    setActiveMaskInput(null);
    
    // Reset Extension Utils
    setExtFirstFrame('');
    setExtLastFrame('');
    setExtFirstClip('');
    setExtLastClip('');
    setExtVideoUrl('');
    setExtControlCondition('');
    setExtPromptExtend(false);
    setExtSeed(undefined);
    setExtWatermark(false);

    // Reset Outpainting Utils
    setOutTopScale(1.0);
    setOutBottomScale(1.0);
    setOutLeftScale(1.0);
    setOutRightScale(1.0);

    setUiStatus('idle');
    setErrorMsg('');
    setSuccessMsg('');
    // 不清除 tasks，因为是历史记录
  };

  const isSubmitDisabled =
    uiStatus === 'uploading' ||
    uiStatus === 'submitting' ||
    uploadingVideo ||
    uploadingExt;

  // 全部必填参数就绪判定
  const isSubmitReady = useMemo(() => {
    const hasPrompt = !!prompt.trim();
    if (!hasPrompt) return false;

    if (functionMode === 'image_reference') {
      return refImages.length >= 1;
    }

    if (functionMode === 'video_repainting') {
      return !!videoUrl && !!controlCondition && !uploadingVideo;
    }

    if (functionMode === 'video_edit') {
      // 掩码为可选，要求原视频存在
      return !!videoUrl && !uploadingVideo;
    }

    if (functionMode === 'video_extension') {
      // 参考视频 + 控制条件 或 帧对/片段对其一
      const hasRefVideo = !!extVideoUrl && !!extControlCondition;
      const hasFramePair = !!extFirstFrame && !!extLastFrame;
      const hasClipPair = !!extFirstClip && !!extLastClip;
      return (hasRefVideo || hasFramePair || hasClipPair) && !uploadingExt;
    }

    if (functionMode === 'video_outpainting') {
      return !!videoUrl && !uploadingVideo;
    }

    return false;
  }, [prompt, functionMode, refImages.length, videoUrl, controlCondition, uploadingVideo, extVideoUrl, extControlCondition, extFirstFrame, extLastFrame, extFirstClip, extLastClip, uploadingExt]);

  return (
    <Box sx={{ width: '100%' }}>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          component="img"
          src="/vedioedit.svg"
          alt="video edit"
          sx={{ width: 44, height: 32, mr: 1 }}
        />
        <Typography variant="h6" sx={{ fontWeight: 900, color: '#f97316', textShadow: '1px 1px 0 #000, -1px 1px 0 #000, 1px -1px 0 #000, -1px -1px 0 #000' }}>
          视频编辑
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'flex-start', pl: 0, mt: 0 }}>
      {/* ========== 左侧工作台 ========== */}
      <Paper
        elevation={0}
        sx={{
          width: '400px',
          height: '680px', // Fixed height to prevent layout shift
          flexShrink: 0,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.6)',
          borderRadius: '16px',
          '&:hover .wb-scroll': { overflowY: 'auto' },
        }}
      >
        <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.13rem' }}>
              工作台
            </Typography>
        </Box>

        <Box className="wb-scroll" sx={{ flex: 1, overflowY: 'hidden', overflowX: 'hidden', pr: 1, mr: -1 }}>
        <Stack spacing={3}>
          {/* 功能模式选择 */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
              功能模式
            </Typography>
            <TextField
              select
              fullWidth
              disabled={isSubmitDisabled}
              size="small"
              value={functionMode}
              InputProps={{ sx: { fontSize: '0.8rem' } }}
              onChange={(e) => {
                  const mode = e.target.value as FunctionMode;
                  setFunctionMode(mode);
                  setRefImages([]);
                  setVideoUrl('');
                  setLocalVideoPreviewUrl('');
                  setErrorMsg('');
                  setMaskImageUrl('');
                  setMaskVideoUrl('');
                  setActiveMaskInput(null);
                  
                  // Reset extension states
                  setExtFirstFrame('');
                  setExtLastFrame('');
                  setExtFirstClip('');
                  setExtLastClip('');
                  setExtVideoUrl('');
                  setExtControlCondition('');
                  setExtPromptExtend(false); // User recommended false, but code default was false?
                  setExtSeed(undefined);
                  setExtWatermark(false);

                  // Reset outpainting states
                  setOutTopScale(1.0);
                  setOutBottomScale(1.0);
                  setOutLeftScale(1.0);
                  setOutRightScale(1.0);

                  // Reset control condition based on mode
                  if (mode === 'video_edit') {
                      setControlCondition('' as any);
                  } else if (mode === 'video_extension') {
                      setExtPromptExtend(false); // Default recommendation
                  } else {
                      setControlCondition('depth');
                  }
              }}
            >
              <MenuItem value="image_reference" sx={{ fontSize: '0.8rem' }}>图片生成视频 (Image Reference)</MenuItem>
              <MenuItem value="video_repainting" sx={{ fontSize: '0.8rem' }}>视频重绘 (Video Repainting)</MenuItem>
              <MenuItem value="video_edit" sx={{ fontSize: '0.8rem' }}>局部编辑 (Video Edit)</MenuItem>
              <MenuItem value="video_extension" sx={{ fontSize: '0.8rem' }}>视频延展 (Video Extension)</MenuItem>
              <MenuItem value="video_outpainting" sx={{ fontSize: '0.8rem' }}>视频扩画 (Video Outpainting)</MenuItem>
          </TextField>
          </Box>

          {/* Prompt 输入 */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
              提示词 (Prompt) *
            </Typography>
            <TextField
                placeholder="请输入视频生成/编辑提示词"
                multiline
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={isSubmitDisabled}
                fullWidth
                InputProps={{ sx: { fontSize: '0.8rem' } }}
            />
          </Box>

          {/* ==================== 模式特定字段 ==================== */}
          
          {/* 1. Image Reference 模式 */}
          {functionMode === 'image_reference' && (
              <>
                {/* Size 选择 */}
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
                    视频尺寸
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        disabled={isSubmitDisabled}
                        size="small"
                        value={size}
                        InputProps={{ sx: { fontSize: '0.8rem' } }}
                        onChange={(e) => setSize(e.target.value as SizeOption)}
                    >
                    <MenuItem value="1280*720" sx={{ fontSize: '0.8rem' }}>1280*720 (16:9)</MenuItem>
                    <MenuItem value="720*1280" sx={{ fontSize: '0.8rem' }}>720*1280 (9:16)</MenuItem>
                    <MenuItem value="960*960" sx={{ fontSize: '0.8rem' }}>960*960 (1:1)</MenuItem>
                    <MenuItem value="832*1088" sx={{ fontSize: '0.8rem' }}>832*1088 (3:4)</MenuItem>
                    <MenuItem value="1088*832" sx={{ fontSize: '0.8rem' }}>1088*832 (4:3)</MenuItem>
                </TextField>
                </Box>

                {/* 参考图片上传按钮 (image_reference) */}
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} gutterBottom>
                    参考图片 (1-3张) *
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    点击图片下方的标签可切换类型：obj (主体) 或 bg (背景)，最多只能有1个背景
                    </Typography>

                    <Button
                    variant="outlined"
                    component="label"
                    disabled={isSubmitDisabled || refImages.length >= 3}
                    sx={{ mb: 2 }}
                    >
                    {uiStatus === 'uploading' ? '上传中...' : '上传图片'}
                    <input type="file" hidden accept="image/*" multiple onChange={handleImageUpload} />
                    </Button>
                 </Box>
              </>
          )}

          {/* 2. Video Repainting 模式 */}
          {functionMode === 'video_repainting' && (
              <>
                 {/* Video Upload */}
                 <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>
                    原视频 *
                    </Typography>
                    <Box
                      component="div"
                      onClick={() => { if (!isSubmitDisabled) { mainVideoInputRef.current?.click(); } }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '140px',
                            border: '2px dashed #cbd5e1',
                            borderRadius: 2,
                            bgcolor: '#f8fafc',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: '#3b82f6',
                                bgcolor: '#f0f9ff',
                            },
                            '&:hover .clear-btn': {
                              opacity: 1,
                            },
                        }}
                    >
                        {(localVideoPreviewUrl || videoUrl) ? (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#000' }}>
                                <video
                              src={localVideoPreviewUrl || videoUrl}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        display: 'block',
                                    }}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                />
                                {/* 成功后不显示额外提示文案 */}
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center' }}>
                                <CloudUploadIcon sx={{ fontSize: 32, mb: 1, color: '#ff9800' }} />
                                <Typography variant="body2" sx={{ 
                                  fontWeight: 500, 
                                  fontSize: '0.75rem',
                                  color: '#555',
                                  '&:hover': {
                                    color: '#3b82f6'
                                  }
                                }}>
                                  {uploadingVideo ? '上传中...' : '点击或拖拽视频上传'}
                                </Typography>
                            </Box>
                        )}
                        {(localVideoPreviewUrl || videoUrl) && (
                          <IconButton
                            className="clear-btn"
                              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleClearMainVideo(); }}
                            size="small"
                            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, opacity: 0, transition: 'opacity 0.2s ease', bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                            <input ref={mainVideoInputRef} type="file" hidden accept="video/*" onChange={handleVideoUpload} disabled={isSubmitDisabled} />
                    </Box>
                 </Box>

                 {/* Control Condition */}
                 <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
                    控制条件 *
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        disabled={isSubmitDisabled}
                        size="small"
                        value={controlCondition}
                        InputProps={{ sx: { fontSize: '0.8rem' } }}
                        onChange={(e) => setControlCondition(e.target.value as ControlCondition)}
                    >
                        <MenuItem value="posebodyface" sx={{ fontSize: '0.8rem' }}>身体与表情 (posebodyface)</MenuItem>
                        <MenuItem value="posebody" sx={{ fontSize: '0.8rem' }}>身体姿态 (posebody)</MenuItem>
                        <MenuItem value="depth" sx={{ fontSize: '0.8rem' }}>深度结构 (depth)</MenuItem>
                        <MenuItem value="scribble" sx={{ fontSize: '0.8rem' }}>线稿结构 (scribble)</MenuItem>
                 </TextField>
                 </Box>

                 {/* Strength */}
                 <Box>
                    <Typography id="strength-slider" variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>
                        重绘强度: {strength}
                    </Typography>
                    <Box sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: 1, px: 2, height: '40px', display: 'flex', alignItems: 'center', '&:hover': { borderColor: 'text.primary' } }}>
                        <Slider
                            size="small"
                            value={strength}
                            onChange={(_, val) => setStrength(val as number)}
                            step={0.05}
                            min={0.0}
                            max={1.0}
                            valueLabelDisplay="auto"
                            disabled={isSubmitDisabled}
                            aria-labelledby="strength-slider"
                            sx={{
                                '& .MuiSlider-thumb': {
                                    width: 12,
                                    height: 12,
                                },
                            }}
                        />
                    </Box>
                 </Box>
              </>
          )}

          {/* 3. Video Edit 模式 (New) */}
          {functionMode === 'video_edit' && (
              <>
                {/* Video Upload */}
                 <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>
                    原视频 *
                    </Typography>
                    <Box
                      component="div"
                      onClick={() => { if (!isSubmitDisabled) { mainVideoInputRef.current?.click(); } }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '140px',
                            border: '2px dashed #cbd5e1',
                            borderRadius: 2,
                            bgcolor: '#f8fafc',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: '#3b82f6',
                                bgcolor: '#f0f9ff',
                            },
                            '&:hover .clear-btn': {
                              opacity: 1,
                            },
                        }}
                    >
                        {(localVideoPreviewUrl || videoUrl) ? (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#000' }}>
                                <video
                              src={localVideoPreviewUrl || videoUrl}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        display: 'block',
                                    }}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                />
                                {/* 成功后不显示额外提示文案 */}
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center' }}>
                                <CloudUploadIcon sx={{ fontSize: 32, mb: 1, color: '#ff9800' }} />
                                <Typography variant="body2" sx={{ 
                                  fontWeight: 500, 
                                  fontSize: '0.75rem',
                                  color: '#555',
                                  '&:hover': {
                                    color: '#3b82f6'
                                  }
                                }}>
                                  {uploadingVideo ? '上传中...' : '点击或拖拽视频上传'}
                                </Typography>
                            </Box>
                        )}
                        {(localVideoPreviewUrl || videoUrl) && (
                          <IconButton
                            className="clear-btn"
                              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleClearMainVideo(); }}
                            size="small"
                            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, opacity: 0, transition: 'opacity 0.2s ease', bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                            <input ref={mainVideoInputRef} type="file" hidden accept="video/*" onChange={handleVideoUpload} disabled={isSubmitDisabled} />
                    </Box>
                 </Box>

                 {/* Mask Selection Grid */}
                 <Box>
                     <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>掩码输入</Typography>
                     <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                         {/* Mask Image Option */}
                         <Paper 
                          elevation={0}
                          sx={{ 
                            flex: 1, p: 1, textAlign: 'center', cursor: 'pointer',
                            border: activeMaskInput === 'image' ? '2px solid #1976d2' : '1px solid #ddd',
                            bgcolor: activeMaskInput === 'image' ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                            boxShadow: 'none'
                          }}
                            onClick={() => {
                                setActiveMaskInput(activeMaskInput === 'image' ? null : 'image');
                                setMaskVideoUrl(''); // clear other
                            }}
                         >
                               <Typography sx={{ fontSize: '0.8rem' }}>掩码图像</Typography>
                         </Paper>
                         
                         {/* Mask Video Option */}
                         <Paper 
                          elevation={0}
                          sx={{ 
                            flex: 1, p: 1, textAlign: 'center', cursor: 'pointer',
                            border: activeMaskInput === 'video' ? '2px solid #1976d2' : '1px solid #ddd',
                            bgcolor: activeMaskInput === 'video' ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                            boxShadow: 'none'
                          }}
                             onClick={() => {
                                setActiveMaskInput(activeMaskInput === 'video' ? null : 'video');
                                setMaskImageUrl(''); // clear other
                            }}
                         >
                             <Typography sx={{ fontSize: '0.8rem' }}>掩码视频</Typography>
                         </Paper>
                     </Stack>

                     {/* Mask Inputs based on selection */}
                     {activeMaskInput === 'image' && (
                         <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, mb: 2 }}>
                             <Box display="flex" alignItems="center" justifyContent="space-between">
                               <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>上传掩码图像</Typography>
                               <IconButton size="small" onClick={() => openMaskEditor('image')} title="没有掩码图像? 点击上传制作">
                                 <Box component="img" src="/mask.svg" alt="mask" sx={{ width: 20, height: 20 }} />
                               </IconButton>
                             </Box>
                             <Box
                               component="div"
                               onClick={() => { if (!isSubmitDisabled) { maskImageInputRef.current?.click(); } }}
                               sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 width: '100%',
                                 aspectRatio: '16 / 9',
                                 border: '2px dashed #cbd5e1',
                                 borderRadius: 2,
                                 bgcolor: '#f8fafc',
                                 cursor: 'pointer',
                                 position: 'relative',
                                 overflow: 'hidden',
                                 transition: 'all 0.3s ease',
                                 '&:hover': { borderColor: '#3b82f6', bgcolor: '#f0f9ff' },
                                 mb: 2,
                                 '&:hover .clear-btn': { opacity: 1 },
                               }}
                             >
                               {(localMaskImagePreviewUrl || maskImageUrl) ? (
                                 <img
                                   src={localMaskImagePreviewUrl || maskImageUrl}
                                   alt="mask"
                                   style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', backgroundColor: '#000' }}
                                 />
                               ) : (
                                 <Box sx={{ textAlign: 'center' }}>
                                   <CloudUploadIcon sx={{ fontSize: 32, mb: 1, color: '#ff9800' }} />
                                   <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', color: '#555' }}>
                                     点击或拖拽图片上传
                                   </Typography>
                                 </Box>
                               )}
                               {(localMaskImagePreviewUrl || maskImageUrl) && (
                                 <IconButton
                                   className="clear-btn"
                                   onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                   onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleClearMaskImage(); }}
                                   size="small"
                                   sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, opacity: 0, transition: 'opacity 0.2s ease', bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
                                 >
                                   <CloseIcon fontSize="small" />
                                 </IconButton>
                               )}
                               <input ref={maskImageInputRef} type="file" hidden accept="image/*" onChange={handleMaskImageUpload} disabled={isSubmitDisabled} />
                             </Box>
                             <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
                                    掩码帧 ID (Mask Frame ID)
                                </Typography>
                                <TextField 
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={maskFrameId}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '') {
                                            setMaskFrameId('');
                                            return;
                                        }
                                        let num = parseInt(val);
                                        if (isNaN(num)) return; // Should not happen with type=number usually
                                        if (num > 81) num = 81;
                                        if (num < 1) num = 1;
                                        setMaskFrameId(num);
                                    }}
                                    onBlur={() => {
                                        if (maskFrameId === '') {
                                            setMaskFrameId(1);
                                        }
                                    }}
                                    helperText="标识掩码目标出现在视频中的哪一帧 (默认为1, 最大81)"
                                    InputProps={{ sx: { fontSize: '0.8rem' }, inputProps: { max: 81, min: 1 } }}
                                />
                             </Box>
                         </Box>
                     )}

                     {activeMaskInput === 'video' && (
                         <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, mb: 2 }}>
                             <Box display="flex" alignItems="center" justifyContent="space-between">
                                 <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>上传掩码视频</Typography>
                             </Box>
                             <Box
                               component="div"
                               onClick={() => { if (!isSubmitDisabled) { maskVideoInputRef.current?.click(); } }}
                               sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 width: '100%',
                                 aspectRatio: '16 / 9',
                                 border: '2px dashed #cbd5e1',
                                 borderRadius: 2,
                                 bgcolor: '#f8fafc',
                                 cursor: 'pointer',
                                 position: 'relative',
                                 overflow: 'hidden',
                                 transition: 'all 0.3s ease',
                                 '&:hover': { borderColor: '#3b82f6', bgcolor: '#f0f9ff' },
                                 '&:hover .clear-btn': { opacity: 1 },
                               }}
                             >
                               {(localMaskVideoPreviewUrl || maskVideoUrl) ? (
                                 <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#000' }}>
                                   <video
                                     src={localMaskVideoPreviewUrl || maskVideoUrl}
                                     style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
                                     autoPlay
                                     muted
                                     loop
                                     playsInline
                                   />
                                 </Box>
                               ) : (
                                 <Box sx={{ textAlign: 'center' }}>
                                   <CloudUploadIcon sx={{ fontSize: 32, mb: 1, color: '#ff9800' }} />
                                   <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', color: '#555' }}>
                                     点击或拖拽视频上传
                                   </Typography>
                                 </Box>
                               )}
                               {(localMaskVideoPreviewUrl || maskVideoUrl) && (
                                 <IconButton
                                   className="clear-btn"
                                   onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                   onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleClearMaskVideo(); }}
                                   size="small"
                                   sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, opacity: 0, transition: 'opacity 0.2s ease', bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
                                 >
                                   <CloseIcon fontSize="small" />
                                 </IconButton>
                               )}
                               <input ref={maskVideoInputRef} type="file" hidden accept="video/*" onChange={handleMaskVideoUpload} disabled={isSubmitDisabled} />
                             </Box>
                         </Box>
                     )}

                    {/* 掩码编辑器弹窗（仅用于掩码图像） */}
                     <Dialog 
                        open={showMaskEditor && maskEditorMode === 'image'} 
                           onClose={closeMaskEditor}
                           maxWidth="lg"
                           fullWidth
                           PaperProps={{
                             sx: {
                               maxWidth: '1250px !important',
                               maxHeight: '90vh',
                               borderRadius: 2
                             }
                           }}
                         >
                           <DialogTitle sx={{ m: 0, p: 2 }}>
                             <Typography sx={{ fontWeight: 700 }}>
                          没有掩码图像? 点击上传制作
                             </Typography>
                             <IconButton
                                onClick={closeMaskEditor}
                                sx={{
                                  position: 'absolute',
                                  right: 8,
                                  top: 8,
                                  color: (theme) => theme.palette.grey[500],
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                           </DialogTitle>
                           
                           <DialogContent dividers sx={{ p: 0, height: '600px', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}> 
                               {/* Canvas / Video 区域 (居中) */}
                          <Box sx={{ flex: 1, width: '100%', overflow: 'auto', bgcolor: '#333', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                           <Box sx={{ position: 'relative', boxShadow: 3, display: maskEditorImageUrl ? 'block' : 'none' }}>
                            <canvas ref={baseCanvasRef} style={{ display: 'block' }} />
                            <canvas
                              ref={drawCanvasRef}
                              style={{ position: 'absolute', left: 0, top: 0, cursor: maskTool==='pen'?'crosshair':'cell' }}
                              onMouseDown={onDrawStart}
                              onMouseMove={onDrawMove}
                              onMouseUp={onDrawEnd}
                              onMouseLeave={onDrawEnd}
                            />
                            <canvas ref={maskCanvasRef} style={{ display: 'none' }} />
                           </Box>
                                  {/* Empty state hint */}
                           {!maskEditorImageUrl && (
                             <Typography variant="body1" sx={{ color: '#aaa', position: 'absolute' }}>
                               请点击下方按钮上传图片
                             </Typography>
                           )}
                               </Box>
                           </DialogContent>

                           <DialogActions sx={{ p: 2, justifyContent: 'space-between', bgcolor: '#fff' }}>
                                 <Box>
                             <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => maskEditorInputRef.current?.click()}>
                               上传图片
                             </Button>
                                    <input 
                                        ref={maskEditorInputRef} 
                                        type="file" 
                                        hidden 
                               accept={'image/*'} 
                                        onChange={handleMaskEditorFileUploadWrapper} 
                                    />
                                 </Box>
                                 
                          {/* Image Mode Tools */}
                          {maskEditorMode === 'image' && (
                                     <Box display="flex" alignItems="center" gap={2}>
                                       {/* <IconButton size="small" color={maskTool==='move'?'primary':'default'} onClick={() => setMaskTool('move')} title="移动/选择"><Box component="span" sx={{ fontSize: '1.2rem' }}>✋</Box></IconButton> */}
                                       <IconButton size="small" color={maskTool==='pen'?'primary':'default'} onClick={() => setMaskTool('pen')} title="画笔"><BrushIcon /></IconButton>
                                       <IconButton size="small" color={maskTool==='eraser'?'primary':'default'} onClick={() => setMaskTool('eraser')} title="橡皮"><Box component="span" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>⌫</Box></IconButton>
                                       {/* Enable AutoFill for both Image and Video modes */}
                                       <FormControlLabel
                                          control={<Switch size="small" checked={isAutoFill} onChange={(e) => setIsAutoFill(e.target.checked)} />}
                                          label={<Typography variant="caption" sx={{ userSelect: 'none' }}>自动填充</Typography>}
                                       />
                                       <Slider 
                                          size="small"
                                          value={maskBrushSize}
                                          min={1} max={100}
                                          onChange={(_, v) => setMaskBrushSize(v as number)}
                                          sx={{ width: 100 }}
                                          valueLabelDisplay="auto"
                                       />
                                       <IconButton size="small" onClick={onUndoMask} title="撤回"><UndoIcon /></IconButton>
                                       <IconButton size="small" color="error" onClick={handClearMaskEditor} title="清除所有"><DeleteIcon /></IconButton>
                                     </Box>
                                 )}

                                 <Button variant="contained" onClick={handleEditorGenerate}>
                                     {uiStatus === 'uploading' ? '生成/上传中...' : '生成并使用'}
                                 </Button>
                           </DialogActions>
                         </Dialog>
                 </Box>

                 {/* Parameters */}
                 <Stack spacing={2}>
                     {/* Control Condition (Optional) */}
                     <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
                        控制条件
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            disabled={isSubmitDisabled}
                            size="small"
                            value={controlCondition || ''}
                            SelectProps={{ displayEmpty: true }}
                            InputProps={{ sx: { fontSize: '0.8rem' } }}
                            onChange={(e) => setControlCondition(e.target.value as ControlCondition)}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>无 (None)</em></MenuItem>
                            <MenuItem value="posebodyface" sx={{ fontSize: '0.8rem' }}>身体与表情 (posebodyface)</MenuItem>
                            <MenuItem value="depth" sx={{ fontSize: '0.8rem' }}>深度结构 (depth)</MenuItem>
                        </TextField>
                     </Box>

                     {/* Mask Type - Show when mask image is uploaded */}
                     {maskImageUrl && (
                         <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
                            掩码编辑方式 (Mask Type)
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                disabled={isSubmitDisabled}
                                size="small"
                                value={maskType}
                                InputProps={{ sx: { fontSize: '0.8rem' } }}
                                onChange={(e) => setMaskType(e.target.value as MaskType)}
                            >
                                <MenuItem value="tracking" sx={{ fontSize: '0.8rem' }}>动态跟随 (tracking)</MenuItem>
                                <MenuItem value="fixed" sx={{ fontSize: '0.8rem' }}>固定位置 (fixed)</MenuItem>
                            </TextField>
                         </Box>
                     )}
                     
                     {/* Expansion Settings - Show when mask image is uploaded and tracking mode is selected */}
                     {maskImageUrl && maskType === 'tracking' && (
                         <Box sx={{ p: 2, borderLeft: '3px solid #1976d2', bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 1 }}>
                             <Typography gutterBottom variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                                 扩展设置
                             </Typography>
                             <Stack spacing={2.5}>
                                 <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
                                    扩展模式
                                    </Typography>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        value={expandMode}
                                        InputProps={{ sx: { fontSize: '0.8rem' } }}
                                        onChange={(e) => setExpandMode(e.target.value as ExpandMode)}
                                    >
                                        <MenuItem value="hull" sx={{ fontSize: '0.8rem' }}>多边形 (hull)</MenuItem>
                                        <MenuItem value="bbox" sx={{ fontSize: '0.8rem' }}>边界框 (bbox)</MenuItem>
                                        <MenuItem value="original" sx={{ fontSize: '0.8rem' }}>原始 (original)</MenuItem>
                                    </TextField>
                                 </Box>
                                 <Box>
                                     <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                         扩展比例: {expandRatio}
                                     </Typography>
                                     <Slider 
                                        value={expandRatio} 
                                        min={0} max={1} step={0.01} 
                                        onChange={(_, v) => setExpandRatio(v as number)}
                                        valueLabelDisplay="auto"
                                        sx={{ 
                                            mt: 1,
                                            '& .MuiSlider-thumb': {
                                                width: 12,
                                                height: 12,
                                            },
                                        }}
                                     />
                                 </Box>
                             </Stack>
                         </Box>
                     )}

                    {/* Size 选择 */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
                        输出尺寸
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            disabled={isSubmitDisabled}
                            size="small"
                            value={size}
                            InputProps={{ sx: { fontSize: '0.8rem' } }}
                            onChange={(e) => setSize(e.target.value as SizeOption)}
                        >
                            <MenuItem value="1280*720" sx={{ fontSize: '0.8rem' }}>1280*720 (16:9)</MenuItem>
                            <MenuItem value="720*1280" sx={{ fontSize: '0.8rem' }}>720*1280 (9:16)</MenuItem>
                            <MenuItem value="960*960" sx={{ fontSize: '0.8rem' }}>960*960 (1:1)</MenuItem>
                            <MenuItem value="832*1088" sx={{ fontSize: '0.8rem' }}>832*1088 (3:4)</MenuItem>
                            <MenuItem value="1088*832" sx={{ fontSize: '0.8rem' }}>1088*832 (4:3)</MenuItem>
                        </TextField>
                    </Box>
                 </Stack>
              </>
          )}

          {/* 4. Video Extension 模式 */}
          {functionMode === 'video_extension' && (
              <Stack spacing={3}>


                  {/* 上传区域 */}
                  <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>
                          媒体输入
                      </Typography>
                      <Typography variant="caption" color="text.secondary" paragraph>
                          请根据需要上传首尾帧图像或视频片段。支持 MP4 视频 (≤3秒) 和常规图片。
                      </Typography>

                      <Stack spacing={2}>
                          {/* 首帧 / 尾帧 */}
                          <Stack direction="row" spacing={2}>
                              <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" gutterBottom>首帧图像 (First Frame)</Typography>
                                  <Button component="label" variant="outlined" fullWidth size="small" startIcon={<CloudUploadIcon sx={{ color: '#ff9800' }} />}>
                                      {extFirstFrame ? '已上传' : '上传图片'}
                                      <input type="file" hidden accept="image/*" onChange={(e) => handleExtFileUpload(e, setExtFirstFrame, 'image')} />
                                  </Button>
                                  {extFirstFrame && <Chip label="First Frame OK" size="small" onDelete={() => setExtFirstFrame('')} sx={{ mt: 1 }} />}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" gutterBottom>尾帧图像 (Last Frame)</Typography>
                                  <Button component="label" variant="outlined" fullWidth size="small" startIcon={<CloudUploadIcon sx={{ color: '#ff9800' }} />}>
                                      {extLastFrame ? '已上传' : '上传图片'}
                                      <input type="file" hidden accept="image/*" onChange={(e) => handleExtFileUpload(e, setExtLastFrame, 'image')} />
                                  </Button>
                                  {extLastFrame && <Chip label="Last Frame OK" size="small" onDelete={() => setExtLastFrame('')} sx={{ mt: 1 }} />}
                              </Box>
                          </Stack>

                          {/* 首段 / 尾段 */}
                          <Stack direction="row" spacing={2}>
                              <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" gutterBottom>首段视频 (First Clip, ≤3s)</Typography>
                                  <Button component="label" variant="outlined" fullWidth size="small" startIcon={<CloudUploadIcon sx={{ color: '#ff9800' }} />}>
                                      {extFirstClip ? '已上传' : '上传视频'}
                                      <input type="file" hidden accept="video/mp4" onChange={(e) => handleExtFileUpload(e, setExtFirstClip, 'video')} />
                                  </Button>
                                  {extFirstClip && <Chip label="First Clip OK" size="small" onDelete={() => setExtFirstClip('')} sx={{ mt: 1 }} />}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" gutterBottom>尾段视频 (Last Clip, ≤3s)</Typography>
                                  <Button component="label" variant="outlined" fullWidth size="small" startIcon={<CloudUploadIcon sx={{ color: '#ff9800' }} />}>
                                      {extLastClip ? '已上传' : '上传视频'}
                                      <input type="file" hidden accept="video/mp4" onChange={(e) => handleExtFileUpload(e, setExtLastClip, 'video')} />
                                  </Button>
                                  {extLastClip && <Chip label="Last Clip OK" size="small" onDelete={() => setExtLastClip('')} sx={{ mt: 1 }} />}
                              </Box>
                          </Stack>
                      </Stack>
                  </Box>

                  {/* 运动参考视频 */}
                  <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>
                          运动参考 (Motion Reference)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" paragraph>
                          上传视频提取动态特征指导生成。
                      </Typography>
                      
                      <Button component="label" variant="outlined" startIcon={<CloudUploadIcon sx={{ color: '#ff9800' }} />}>
                          {extVideoUrl ? '更换视频' : '上传参考视频'}
                          <input type="file" hidden accept="video/mp4" onChange={(e) => handleExtFileUpload(e, setExtVideoUrl, 'video')} />
                      </Button>
                      
                      {extVideoUrl && (
                          <Box sx={{ mt: 2 }}>
                              <Chip label="参考视频已就绪" color="info" onDelete={() => { setExtVideoUrl(''); setExtControlCondition(''); }} />
                              
                              <Box sx={{ mt: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }}>
                                    控制条件 *
                                  </Typography>
                                  <TextField
                                      select
                                      fullWidth
                                      size="small"
                                      value={extControlCondition}
                                      onChange={(e) => setExtControlCondition(e.target.value as any)}
                                      InputProps={{ sx: { fontSize: '0.8rem' } }}
                                  >
                                      <MenuItem value="posebodyface" sx={{ fontSize: '0.8rem' }}>身体与表情 (posebodyface)</MenuItem>
                                      <MenuItem value="depth" sx={{ fontSize: '0.8rem' }}>深度结构 (depth)</MenuItem>
                                  </TextField>
                              </Box>
                          </Box>
                      )}
                  </Box>

                  {/* 高级参数 (隐藏，使用默认值) */}
                  {/* 
                  <Box>
                      <Typography variant="subtitle2" gutterBottom>高级参数</Typography>
                      <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
                          <FormControlLabel
                              control={<Switch checked={extPromptExtend} onChange={(e) => setExtPromptExtend(e.target.checked)} />}
                              label={<Typography variant="body2">Prompt 智能扩写</Typography>}
                          />
                          <FormControlLabel
                              control={<Switch checked={extWatermark} onChange={(e) => setExtWatermark(e.target.checked)} />}
                              label={<Typography variant="body2">添加水印</Typography>}
                          />
                      </Stack>
                      <TextField
                          label="随机种子 (Seed)"
                          type="number"
                          size="small"
                          fullWidth
                          value={extSeed === undefined ? '' : extSeed}
                          onChange={(e) => {
                              const v = e.target.value;
                              setExtSeed(v === '' ? undefined : parseInt(v));
                          }}
                          placeholder="留空自动随机"
                      />
                  </Box> 
                  */}
              </Stack>
          )}

          {/* 5. Video Outpainting 模式 */}
          {functionMode === 'video_outpainting' && (
              <Stack spacing={3}>


                  {/* 视频上传 */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>
                    原视频 *
                    </Typography>
                    <Box
                      component="div"
                      onClick={() => { if (!isSubmitDisabled) { mainVideoInputRef.current?.click(); } }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '140px',
                            border: '2px dashed #cbd5e1',
                            borderRadius: 2,
                            bgcolor: '#f8fafc',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: '#3b82f6',
                                bgcolor: '#f0f9ff',
                            },
                            '&:hover .clear-btn': {
                              opacity: 1,
                            },
                        }}
                    >
                        {(localVideoPreviewUrl || videoUrl) ? (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#000' }}>
                                <video
                              src={localVideoPreviewUrl || videoUrl}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        display: 'block',
                                    }}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                />
                                {/* 成功后不显示额外提示文案 */}
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center' }}>
                                <CloudUploadIcon sx={{ fontSize: 32, mb: 1, color: '#ff9800' }} />
                                <Typography variant="body2" sx={{ 
                                  fontWeight: 500, 
                                  fontSize: '0.75rem',
                                  color: '#555',
                                  'label:hover &': {
                                    color: '#3b82f6'
                                  }
                                }}>
                                  {uploadingVideo ? '上传中...' : '点击或拖拽视频上传'}
                                </Typography>
                            </Box>
                        )}
                        {(localVideoPreviewUrl || videoUrl) && (
                          <IconButton
                            className="clear-btn"
                            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleClearMainVideo(); }}
                            size="small"
                            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, opacity: 0, transition: 'opacity 0.2s ease', bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                        <input ref={mainVideoInputRef} type="file" hidden accept="video/mp4" onChange={handleVideoUpload} disabled={isSubmitDisabled} />
                    </Box>
                 </Box>

                 {/* 扩展比例参数 */}
                 <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                     <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#666' }} gutterBottom>
                         扩展比例 (Scale)
                     </Typography>
                     <Typography variant="caption" color="text.secondary" paragraph>
                         设置上下左右扩展比例 (1.0 - 2.0)，1.0 表示不扩展。
                     </Typography>
                     
                     <Stack spacing={2}>
                         {[
                             { label: '上 (Top)', val: outTopScale, setVal: setOutTopScale },
                             { label: '下 (Bottom)', val: outBottomScale, setVal: setOutBottomScale },
                             { label: '左 (Left)', val: outLeftScale, setVal: setOutLeftScale },
                             { label: '右 (Right)', val: outRightScale, setVal: setOutRightScale },
                         ].map((item, idx) => (
                             <Box key={idx}>
                                 <Typography variant="body2" gutterBottom>
                                     {item.label}: {item.val}
                                 </Typography>
                                 <Slider
                                     value={item.val}
                                     min={1.0}
                                     max={2.0}
                                     step={0.1}
                                     marks
                                     valueLabelDisplay="auto"
                                     onChange={(_, v) => item.setVal(v as number)}
                                     sx={{
                                        '& .MuiSlider-thumb': {
                                            width: 12,
                                            height: 12,
                                        },
                                     }}
                                 />
                             </Box>
                         ))}
                     </Stack>
                 </Box>
              </Stack>
          )}

          {/* 公共部分：参考图片列表展示与上传 (视频模式下作为 Ref Image) */}
          {(functionMode === 'video_repainting' || functionMode === 'video_edit') && (
             <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} gutterBottom>
                参考图片 (最多1张)
                </Typography>
                <Button
                variant="outlined"
                component="label"
                disabled={isSubmitDisabled || refImages.length >= 1}
                sx={{ mb: 2 }}
                >
                {uiStatus === 'uploading' ? '上传中...' : '参考图片'}
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                </Button>
             </Box>
          )}

          {/* 公共部分：参考图片列表展示 */}
          {refImages.length > 0 && (
              <Stack spacing={2}>
                {refImages.map((img, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <img
                        src={img.url}
                        alt={`ref-${index}`}
                        style={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" noWrap>
                          {img.localFile?.name || img.url}
                        </Typography>

                        {/* 仅在 image_reference 模式下显示主体/背景切换 */}
                        {functionMode === 'image_reference' && (
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={img.type === 'obj' ? '主体 (obj)' : '背景 (bg)'}
                                color={img.type === 'obj' ? 'primary' : 'secondary'}
                                size="small"
                                onClick={() => handleToggleImageType(index)}
                                clickable
                                disabled={isSubmitDisabled}
                              />
                            </Box>
                        )}
                      </Box>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteImage(index)}
                        disabled={isSubmitDisabled}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
          )}

          {/* 错误提示 */}
          {errorMsg && (
            <Alert severity="error" onClose={() => setErrorMsg('')}>
              {errorMsg}
            </Alert>
          )}

          {/* 成功提示 */}
          {successMsg && (
            <Alert severity="success" onClose={() => setSuccessMsg('')}>
              {successMsg}
            </Alert>
          )}

        </Stack>
        </Box>

        {/* Footer: 操作按钮 */}
        <Box sx={{ pt: 2, mt: 'auto' }}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={isSubmitDisabled}
              sx={{ flex: 1, height: '40px' }}
            >
              清除
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!isSubmitReady || isSubmitDisabled}
              sx={{ flex: 2, height: '40px' }}
            >
              {uiStatus === 'submitting' ? '提交中...' : '生成（30积分）'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* ========== 任务结果 ========== */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          height: '680px',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.6)',
          bgcolor: 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(20px) saturate(180%)',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: tasks.length === 0 ? 'center' : 'stretch',
          justifyContent: tasks.length === 0 ? 'center' : 'flex-start',
          overflowY: 'auto',
        }}
      >
        {tasks.length === 0 ? (
           <Typography variant="body1" color="text.secondary">
             结果在此处展示
           </Typography>
        ) : (
           <Stack spacing={2} ref={resultsScrollRef}>
               <Typography variant="h6" gutterBottom>
                 任务结果
               </Typography>
               {tasks.map((task) => (
                  <Paper
                    key={task.id}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: task.status === 'SUCCEEDED' ? 'success.main' : task.status === 'FAILED' ? 'error.main' : 'info.main',
                      position: 'relative',
                    }}
                  >
                    {/* 状态徽章移除，统一使用彩色生成中块进行反馈 */}

                    {/* 生成中动画块（与角色形象生成页彩色生成框一致风格） */}
                    {(task.status === 'PENDING' || task.status === 'RUNNING') && (
                      <Box sx={{ mt: 1, ...rainbowMaskContainerSx }}>
                        <Box
                          sx={{
                            ...placeholderCardSx,
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed #cbd5e1',
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ position: 'relative', zIndex: 1, fontWeight: 700, color: '#334155' }}
                          >
                            生成中…
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* 提示信息 */}
                    <Typography variant="body2" color="text.secondary">
                      提示词: {task.prompt}
                    </Typography>

                    {/* 创建时间 */}
                    <Typography variant="caption" color="text.secondary">
                      创建于: {new Date(task.createdAt).toLocaleString()}
                    </Typography>

                    {/* 视频链接 */}
                    {task.videoUrl && (
                      <Button
                        variant="outlined"
                        size="small"
                        href={task.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<CloudUploadIcon sx={{ color: '#ff9800' }} />}
                        sx={{ mt: 1 }}
                      >
                        下载视频
                      </Button>
                    )}

                    {/* 错误信息 */}
                    {task.error && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="error.main">
                          错误: {task.error}
                        </Typography>
                      </Box>
                    )}

                    {/* 删除按钮 */}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setTasks((prev) => prev.filter((t) => t.id !== task.id));
                        // 额外处理：如果当前任务是正在处理的任务，停止轮询
                        if (task.status === 'RUNNING') {
                          // 这里可以调用取消任务的 API，或者在前端标记为已取消
                          setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status: 'FAILED', error: '任务已取消' } : t));
                        }
                      }}
                      sx={{ position: 'absolute', top: 8, left: 8 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
               ))}
           </Stack>
        )}
      </Paper>

      {/* ========== 右侧说明/示例栏目（仅图片生成视频模式） ========== */}
      {(functionMode === 'image_reference' || functionMode === 'video_repainting' || functionMode === 'video_edit' || functionMode === 'video_extension' || functionMode === 'video_outpainting') && (
        <Box
          sx={{
            width: '280px',
            flexShrink: 0,
            p: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {functionMode === 'image_reference' ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                参考图1（参考主体）
              </Typography>
              <img
                src="https://help-static-aliyun-doc.aliyuncs.com/assets/img/zh-CN/2786951571/p955656.png"
                alt="示例图片1"
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                参考图2（参考背景）
              </Typography>
              <img
                src="https://help-static-aliyun-doc.aliyuncs.com/assets/img/zh-CN/2786951571/p955657.png"
                alt="示例图片2"
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输入提示词
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                视频中，一位女孩自晨雾缭绕的古老森林深处款款走出，她步伐轻盈，镜头捕捉她每一个灵动瞬间。当女孩站定，环顾四周葱郁林木时，她脸上绽放出惊喜与喜悦交织的笑容。这一幕，定格在了光影交错的瞬间，记录下女孩与大自然的美妙邂逅。
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输出视频
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250704/fltxcv/20250506150137950398164_52_29_29.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </>
          ) : functionMode === 'video_repainting' ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                原视频
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250704/qzzwek/depth_2.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输入提示词
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                视频展示了一辆黑色的蒸汽朋克风格汽车，绅士驾驶着，车辆装饰着齿轮和铜管。背景是蒸汽驱动的糖果工厂和复古元素，画面复古与趣味
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输出视频
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250704/hyrsqj/depth_2_output.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </>
          ) : functionMode === 'video_edit' ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                原视频
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250703/boyxlu/Inpainting_src_2_new.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                掩码图像
              </Typography>
              <img
                src="https://help-static-aliyun-doc.aliyuncs.com/assets/img/zh-CN/3778351571/p955179.png"
                alt="掩码图像"
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输入提示词
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                视频展示了一家巴黎风情的法式咖啡馆，一只穿着西装的狮子优雅地品着咖啡。它一手端着咖啡杯，轻轻啜饮，神情惬意。咖啡馆装饰雅致，柔和的色调与温暖灯光映照着狮子所在的区域。
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输出视频
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250703/pxhneu/Inpainting_res_2_new.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </>
          ) : functionMode === 'video_extension' ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                原视频
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250515/ocotqq/video_extension_1.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输入提示词
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                一只戴着墨镜的狗在街道上滑滑板，3D卡通。
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输出延长后的视频（5秒）
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250704/ymvqxn/cur_gallery_20250515140027.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                原视频
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250704/hvghxb/cur_gallery_038.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输入提示词
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                一位优雅的女士正在激情演奏小提琴，她身后是一支完整的交响乐团。
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#666' }}>
                输出视频
              </Typography>
              <video
                src="https://help-static-aliyun-doc.aliyuncs.com/file-manage-files/zh-CN/20250704/rnhddk/1d3e788a-1a5d-47d9-9c85-b33793f15cdc.mp4"
                controls
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </>
          )}
        </Box>
      )}
      </Box>

      {/* 积分检查弹窗 */}
      <PointsCheckDialog
        open={showDialog}
        onClose={closeDialog}
        isZeroPoints={dialogProps.isZeroPoints}
      />
    </Box>
  );
}
